/**
 * Customer Relationships section for the supplier settings tab.
 * Manages per-site settings including supplier codes and currency.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Fragment, useState } from 'react';
import { Loader2, Handshake, Archive, Trash2, Pencil } from 'lucide-react';
import { SupplierCustomerRelationship, Site, CustomerSupplierCode, UserProfile, coreAPI, apiErrorMessage } from '../../lib/api';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { BuyerMultiSelect } from './BuyerMultiSelect';
import EditSupplierCodeDialog, { EditingCodeRow, SUPPLIER_CODE_CURRENCIES } from './EditSupplierCodeDialog';

interface CustomerRelationshipsSectionProps {
  relationships: SupplierCustomerRelationship[];
  relLoading: boolean;
  relSaving: number | null;
  canEditSettings: boolean;
  /**
   * Users assignable as buyer, keyed by customer company id. A buyer works for
   * the customer doing the buying, so each relationship draws from its own
   * customer's users — not one shared list.
   */
  buyerOptionsByCustomer: Record<number, UserProfile[]>;
  updateRelationship: (id: number, data: Partial<SupplierCustomerRelationship>) => Promise<void>;
  assignDialogOpen: boolean;
  setAssignDialogOpen: (open: boolean) => void;
  availableSites: Site[];
  selectedSiteId: number | null;
  setSelectedSiteId: (id: number | null) => void;
  assigning: boolean;
  newCodeValue: string;
  setNewCodeValue: (v: string) => void;
  newCodeCurrency: string;
  setNewCodeCurrency: (v: string) => void;
  /** Customers that already hold a relationship — picking one adds a code. */
  assignedCustomerIds: number[];
  setRelationships: React.Dispatch<React.SetStateAction<SupplierCustomerRelationship[]>>;
  openAssignDialog: () => void;
  assignSite: () => void;
}

export function CustomerRelationshipsSection({
  relationships, relLoading,
  relSaving, canEditSettings, buyerOptionsByCustomer, updateRelationship,
  assignDialogOpen, setAssignDialogOpen, availableSites,
  selectedSiteId, setSelectedSiteId, assigning,
  newCodeValue, setNewCodeValue, newCodeCurrency, setNewCodeCurrency,
  assignedCustomerIds,
  setRelationships,
  openAssignDialog, assignSite,
}: CustomerRelationshipsSectionProps) {
  const { isFeatureEnabled, isMenuItemVisible } = useMenuSettings();
  const { user } = useAuth();
  // CR-7272: a customer without Call Off has nothing to say about it, so the
  // column is not shown empty — it is not shown at all. Same test the
  // Purchasing tab and the Suppliers logistics option already use.
  const callOffAvailable = isMenuItemVisible('callOff') && isFeatureEnabled('switch_call_off_tab');

  // Archive and Remove Customer are administrative: helix/superuser, or a
  // customer's own admin. `canEditSettings` is wider than that (any customer
  // user), so it is not the right gate for these two.
  const canManageCodes = Boolean(
    user?.is_superuser || (canEditSettings && user?.is_company_admin),
  );
  const [archiving, setArchiving] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<EditingCodeRow | null>(null);
  // Removing a customer deletes the relationship and every code under it,
  // so it is confirmed first and only offered on the customer's last line.
  const [confirmRemove, setConfirmRemove] = useState<SupplierCustomerRelationship | null>(null);
  const [removing, setRemoving] = useState(false);

  const removeRelationship = async (rel: SupplierCustomerRelationship) => {
    setRemoving(true);
    try {
      await coreAPI.deleteSupplierRelationship(rel.id);
      setRelationships((prev) => prev.filter((r) => r.id !== rel.id));
      toast.success(`${rel.customer_name} removed`);
      setConfirmRemove(null);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to remove relationship'));
    } finally {
      setRemoving(false);
    }
  };

  const onCodeSaved = (relId: number, saved: CustomerSupplierCode, replacedId: number | null) => {
    const codes = relationships.find((r) => r.id === relId)?.supplier_codes ?? [];
    setCodesFor(relId, replacedId !== null ? codes.map((c) => (c.id === replacedId ? saved : c)) : [...codes, saved]);
  };

  const archiveCode = async (relId: number, code: CustomerSupplierCode) => {
    setArchiving(code.id);
    try {
      await coreAPI.archiveSupplierCode(code.id);
      const next = (relationships.find((r) => r.id === relId)?.supplier_codes ?? [])
        .map((c) => (c.id === code.id ? { ...c, is_active: false } : c));
      setCodesFor(relId, next);
      toast.success(`${code.supplier_code || 'Code'} archived`);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to archive the supplier code'));
    } finally {
      setArchiving(null);
    }
  };

  const selectedCustomerAlreadyAssigned =
    selectedSiteId !== null && assignedCustomerIds.includes(selectedSiteId);

  const setCodesFor = (relId: number, codes: CustomerSupplierCode[]) => {
    setRelationships((prev) => prev.map((r) => (r.id === relId ? { ...r, supplier_codes: codes } : r)));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Handshake className="h-5 w-5" />Customer Relationships</CardTitle>
          <CardDescription>Per-company settings including supplier codes and currency</CardDescription>
        </div>
        {canEditSettings && (<Button variant="outline" size="sm" onClick={openAssignDialog}>+ Assign Customer</Button>)}
      </CardHeader>
      <CardContent>
        {relLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : relationships.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Handshake className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No company relationships found</p>
            {canEditSettings && <p className="text-sm mt-1">Click "Assign Customer" to add this supplier to a customer company</p>}
          </div>
        ) : (
          <div className="space-y-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Supplier Code</TableHead>
                  <TableHead>Currency</TableHead>
                  {callOffAvailable && <TableHead>Call-Off</TableHead>}
                  <TableHead>Buyer</TableHead>
                  {canManageCodes && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {relationships.map((rel) => {
                  // CR-7272: every code gets its own line. This used to collapse
                  // a customer's codes into "the primary, +N others", which read
                  // as one real code plus spares — but each code is equally real
                  // and trades in its own currency (one customer holds GBP and
                  // USD codes for the same supplier at the same site), and the
                  // contract-price import validates against exactly that. A
                  // summary that hides the second one hides the thing the user
                  // came to check.
                  const codes = rel.supplier_codes ?? [];
                  const activeCodes = codes.filter((c) => c.is_active);
                  // A relationship with no codes still needs one row, so it is
                  // visible and can be expanded to add its first code.
                  const codeRows: (CustomerSupplierCode | null)[] =
                    activeCodes.length > 0 ? activeCodes : [null];
                  return (
                    // Key belongs on the mapped element itself — the inner
                    // rows carry their own keys but React never saw one on
                    // the fragment, so a second relationship row warned.
                    <Fragment key={rel.id}>
                      {codeRows.map((code) => (
                      <TableRow
                        key={code ? `code-${code.id}` : `rel-${rel.id}-empty`}
                        className="hover:bg-muted/50"
                      >
                        {/* Every row stands on its own: the company name and
                            its per-relationship values repeat against each
                            code. Blanking them on the second code still read
                            as one row with an indented afterthought, which is
                            the grouping this was meant to remove. */}
                        <TableCell className="font-medium">{rel.customer_name}</TableCell>
                        <TableCell>{code?.supplier_code || rel.supplier_code || '-'}</TableCell>
                        <TableCell>{code?.currency || rel.currency || '-'}</TableCell>
                        {callOffAvailable && (
                          <TableCell>
                            <Badge className={rel.call_off_enabled ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-muted text-muted-foreground'}>
                              {rel.call_off_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </TableCell>
                        )}
                        {/* CR-6794: the legacy per-relationship buyer. The
                            supplier's own buyer is set in Management Settings
                            below; this column stays because a customer can
                            still hold a relationship-level buyer from the ERP. */}
                        <TableCell>
                          {canManageCodes ? (
                            <BuyerMultiSelect
                              options={buyerOptionsByCustomer[rel.customer] ?? []}
                              selected={rel.buyers ?? (rel.buyer !== null ? [rel.buyer] : [])}
                              fallbackNames={rel.buyer_names ?? (rel.buyer_name ? [rel.buyer_name] : [])}
                              saving={relSaving === rel.id}
                              disabled={relSaving === rel.id}
                              onChange={(ids) => updateRelationship(rel.id, {
                                buyers: ids,
                              } as Partial<SupplierCustomerRelationship>)}
                            />
                          ) : (
                            (rel.buyer_names?.length ? rel.buyer_names.join(', ') : rel.buyer_name)
                              || <span className="text-muted-foreground italic">Not assigned</span>
                          )}
                        </TableCell>
                        {canManageCodes && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {relSaving === rel.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingRow({
                                  relationshipId: rel.id, customerName: rel.customer_name, code,
                                  fallbackCode: rel.supplier_code ?? '', fallbackCurrency: rel.currency ?? '',
                                })}
                              >
                                <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
                              </Button>
                              {code && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={archiving === code.id}
                                  onClick={() => archiveCode(rel.id, code)}
                                >
                                  {archiving === code.id
                                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    : <Archive className="h-3.5 w-3.5 mr-1.5" />}
                                  Archive
                                </Button>
                              )}
                              {/* Remove Customer takes every line of this
                                  customer with it, so it only sits on the last
                                  one — archive a code to remove just its line. */}
                              {codeRows.length === 1 && (
                                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmRemove(rel)}>
                                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove Customer
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                      ))}

                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Assign Customer Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Customer</DialogTitle>
            <DialogDescription>
              Pick a company to assign this supplier to, or pick one it already
              serves to give it another supplier code and currency.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {availableSites.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No available customers to assign</p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="assign-company">Company</label>
                  <select id="assign-company" value={selectedSiteId ?? ''} onChange={(e) => setSelectedSiteId(e.target.value ? parseInt(e.target.value) : null)} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                    <option value="">Select a company...</option>
                    {availableSites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}{site.code ? ` (${site.code})` : ''}{assignedCustomerIds.includes(site.id) ? ' — already assigned' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="assign-code">Supplier code</label>
                    <Input id="assign-code" value={newCodeValue} onChange={(e) => setNewCodeValue(e.target.value)} placeholder="e.g. GOU002" disabled={assigning} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="assign-currency">Currency</label>
                    <select id="assign-currency" value={newCodeCurrency} onChange={(e) => setNewCodeCurrency(e.target.value)} disabled={assigning} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                      <option value="">Select...</option>
                      {SUPPLIER_CODE_CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                </div>
                {selectedCustomerAlreadyAssigned && (
                  <p className="text-xs text-muted-foreground">
                    This company is already assigned — the code above will be added to it as an additional account.
                  </p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={assignSite} disabled={!selectedSiteId || assigning}>
              {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditSupplierCodeDialog row={editingRow} onClose={() => setEditingRow(null)} onSaved={onCodeSaved} />

      {/* Remove Customer confirmation */}
      <Dialog open={confirmRemove !== null} onOpenChange={(open) => { if (!open && !removing) setConfirmRemove(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {confirmRemove?.customer_name}?</DialogTitle>
            <DialogDescription>
              This supplier will no longer be assigned to {confirmRemove?.customer_name}, and its supplier code for
              that customer is removed with it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)} disabled={removing}>Cancel</Button>
            <Button variant="destructive" onClick={() => confirmRemove && removeRelationship(confirmRemove)} disabled={removing}>
              {removing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Remove Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
